/**
 * DAS Chart Utilities
 * 
 * Helper functions for generating power detector chart configurations
 * dynamically based on device attributes.
 */

import { SiteChartsConfig, ChartSeriesConfig } from '../../../SiteCharts/SiteCharts';
import { ChartColors } from '../../../../theme/chartColors';

export interface PowerDetectorParams {
  remoteEquipmentObjectKey: string; // remoteequipment.object_key (e.g., "NDD9X0061_22-20-49-0201.5.10.2")
  indicators: string[];        // e.g., ["ulPowerDetectorBand1", "ulPowerDetectorBand2"]
  bandNames: string[];         // e.g., ["700", "CELL/ESMR", "AWS3", "PCS", "WCS"]
  bandNumbers: number[];       // e.g., [1, 2, 3, 4, 5] - power detector indices
  yAxisMin: number;            // Minimum Y-axis value (e.g., -150 for MRU UL, -10 for MRU DL)
  yAxisMax: number;            // Maximum Y-axis value (e.g., -20 for MRU UL, 80 for MRU DL)
  divisor?: number;            // Optional divisor to scale raw values (e.g., 1000 to convert to dBm)
}

/**
 * Color palette for band visualization
 * Provides distinct colors for up to 8 bands
 */
const BAND_COLORS = [
  ChartColors.success,      // Green
  ChartColors.info,         // Sky Blue
  ChartColors.warning,      // Dark Teal
  ChartColors.error,        // Pink/Red
  ChartColors.active,       // Bright Green
  ChartColors.pending,      // Orange
  '#9333EA',                // Purple
  '#EC4899',                // Pink
];

/**
 * Gets color for band based on index
 * 
 * @param index - Band index (0-based)
 * @returns Hex color code
 */
function getColorForBandIndex(index: number): string {
  return BAND_COLORS[index % BAND_COLORS.length];
}

/**
 * Creates a chart configuration for power detector visualization
 * 
 * Generates dynamic series configuration based on bands and indicators.
 * Each band becomes a separate line series on the chart.
 * API returns data keyed by indicator names (e.g., "dlPowerBand1"),
 * which are mapped to band names (e.g., "PCS") for display.
 * 
 * @param type - Chart type: 'ul' for uplink, 'dl' for downlink
 * @param params - Power detector parameters (remoteEquipmentObjectKey, indicators, bandNames)
 * @returns Complete SiteChartsConfig for use with SiteCharts component
 * 
 * @example
 * const config = createPowerDetectorChartConfig('ul', {
 *   remoteEquipmentObjectKey: 'NDD9X0061.mru.5.10.2.0',
 *   indicators: ['ulPowerDetectorBand1', 'ulPowerDetectorBand4'],
 *   bandNames: ['700', 'PCS'],
 *   bandNumbers: [1, 4],
 *   yAxisMin: -150,
 *   yAxisMax: -20
 * });
 * // API returns: { timestamp, ulPowerDetectorBand1: -1000, ulPowerDetectorBand4: 9.4 }
 * // Chart displays as: { timestamp, "700": -1000, "PCS": 9.4 }
 */
export function createPowerDetectorChartConfig(
  type: 'ul' | 'dl',
  params: PowerDetectorParams
): SiteChartsConfig {
  const title = type === 'ul' 
    ? 'UL Power' 
    : 'DL Power';
  
  const csvFilename = type === 'ul' 
    ? 'ul_power_detector' 
    : 'dl_power_detector';
  
  // Generate series configuration - one line per band
  // Use indicator names as dataKeys (e.g., "dlPowerBand1", "ulPowerDetectorBand1")
  // UI maps indicators to band names for display
  const series: ChartSeriesConfig[] = params.bandNames.map((bandName, index) => ({
    name: bandName,
    type: 'line',
    yAxisIndex: 0,
    color: getColorForBandIndex(index),
    dataKey: params.indicators[index],  // Use actual indicator name from API response
    isPercentage: false
  }));

  return {
    title,
    apiEndpoint: '/api/das/kpi-timeseries',
    csvFilename,
    chartImageFilename: `${csvFilename}_chart`,
    series,
    yAxes: [
      {
        name: 'Power (dBm)',
        position: 'left',
        min: params.yAxisMin,
        max: params.yAxisMax,
        nameGap: 15,
        formatter: '{value}'
      },
      {
        name: '',
        position: 'right'
      }
    ],
    tableColumns: params.bandNames.map((bandName, index) => ({
      header: bandName,
      dataKey: params.indicators[index],  // Use actual indicator name from API response
      isPercentage: false
    })),
    divisor: params.divisor  // Pass through divisor for data scaling
  };
}

/**
 * Builds additional parameters for chart API request
 * 
 * Converts structured parameters into query parameter format
 * expected by the SiteCharts component.
 * 
 * @param serviceDeviceObjectKey - Device serial number / site ID
 * @param remoteEquipmentObjectKey - RemoteEquipment object_key
 * @param indicators - List of performance indicators to query
 * @returns Record of query parameters for API request
 * 
 * @example
 * const params = buildPowerDetectorParams('NDD9X0061', '22-20-49-0201.5.10.2', [
 *   'ulPowerDetectorBand1',
 *   'ulPowerDetectorBand2'
 * ]);
 * // Returns: { 
 * //   service_device_object_key: 'NDD9X0061',
 * //   remoteequipment_object_key: '22-20-49-0201.5.10.2',
 * //   indicators: 'ulPowerDetectorBand1,ulPowerDetectorBand2' 
 * // }
 */
export function buildPowerDetectorParams(
  serviceDeviceObjectKey: string,
  remoteEquipmentObjectKey: string,
  indicators: string[]
): Record<string, string> {
  return {
    service_device_object_key: serviceDeviceObjectKey,
    remoteequipment_object_key: remoteEquipmentObjectKey,
    indicators: indicators.join(',')
  };
}

/**
 * Extracts band indicator names from device attributes
 * 
 * Helper to build indicator list for devices with numbered bands.
 * 
 * @param prefix - Indicator prefix (e.g., 'ulPowerDetectorBand', 'dlPowerDetectorBand')
 * @param bandNumbers - Array of band numbers (e.g., [1, 2, 3])
 * @returns Array of indicator names
 * 
 * @example
 * const indicators = buildBandIndicators('ulPowerDetectorBand', [1, 2, 3]);
 * // Returns: ['ulPowerDetectorBand1', 'ulPowerDetectorBand2', 'ulPowerDetectorBand3']
 */
export function buildBandIndicators(prefix: string, bandNumbers: number[]): string[] {
  return bandNumbers.map(num => `${prefix}${num}`);
}
